import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Type-to-search field that lists what matches and lets a value be clicked --
 * while still accepting a name nobody has captured yet.
 *
 * Distinct from `SearchSelect`, which owns an id and collapses to a chip: this
 * one owns a *string*, because the feed on a bill may not be in the catalogue
 * yet and the operator must be able to type it.
 *
 * The picker this replaces (inline in AdminHome) rendered its results panel
 * only when a match already existed, so an operator typing while the options
 * were still loading -- or whose wording did not match -- got a dead field with
 * no way to tell a slow fetch from an empty catalogue. Every state is visible
 * here: loading, matches, nothing matching (and what happens if they carry on),
 * and a failed load with a retry.
 */

export type SearchOrTypeOption = {
  /** Stable key for the row. */
  key: string;
  /** The value written back on select. */
  value: string;
  /** Second line under the value. */
  detail?: string;
  /** Small badge on the right, e.g. "catalogue" or "3 deals". */
  badge?: string;
};

export type SearchOrTypeProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchOrTypeOption[];
  /** Options are still being fetched. */
  loading?: boolean;
  /** The fetch failed; the panel offers onRetry. */
  failed?: boolean;
  onRetry?: () => void;
  /** Called the first time the field is opened, to fetch options lazily. */
  onOpen?: () => void;
  placeholder?: string;
  /** Says what happens to a name that matches nothing. */
  freeTextNote?: string;
  /** Copy for an empty option list. */
  emptyNote?: string;
  disabled?: boolean;
  /** Rows shown at once; the panel scrolls beyond it. */
  limit?: number;
  ariaLabel?: string;
};

export default function SearchOrType({
  id, value, onChange, options, loading = false, failed = false, onRetry, onOpen,
  placeholder = 'Search or type a new one', freeTextNote, emptyNote, disabled = false,
  limit = 8, ariaLabel,
}: SearchOrTypeProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const openedRef = useRef(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const needle = value.trim().toLowerCase();
  // An exact hit is not a search in progress -- the operator already has what
  // they wanted, so listing it back at them is noise.
  const exact = useMemo(
    () => needle.length > 0 && options.some((o) => o.value.trim().toLowerCase() === needle),
    [options, needle],
  );
  const matches = useMemo(() => {
    const pool = needle
      ? options.filter((o) => o.value.toLowerCase().includes(needle) || (o.detail || '').toLowerCase().includes(needle))
      : options;
    return pool.slice(0, limit);
  }, [options, needle, limit]);

  useEffect(() => { setActive(0); }, [needle]);

  const openPanel = () => {
    setOpen(true);
    if (!openedRef.current) { openedRef.current = true; onOpen?.(); }
  };

  const choose = (option: SearchOrTypeOption) => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') { setOpen(false); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) { openPanel(); return; }
      if (matches.length === 0) return;
      setActive((prev) => {
        const next = event.key === 'ArrowDown' ? prev + 1 : prev - 1;
        return (next + matches.length) % matches.length;
      });
      return;
    }
    if (event.key === 'Enter' && open && matches.length > 0) {
      event.preventDefault();
      choose(matches[Math.min(active, matches.length - 1)]);
    }
  };

  // In the order an operator needs it: what went wrong, what is coming, what
  // matched, what happens if nothing did.
  const panel = (() => {
    if (failed) {
      return (
        <div className="acombo-note">
          Could not load the list.{' '}
          {onRetry && (
            <button
              type="button"
              className="font-semibold text-avcs-primary underline"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onRetry}
            >
              Try again
            </button>
          )}{' '}
          You can still type a name.
        </div>
      );
    }
    if (loading) return <div className="acombo-note">Loading…</div>;
    if (matches.length === 0) {
      // An empty option list can also mean a request that came back empty --
      // `fetchFeedOptions` answers a rate-limited call with its (empty) cache
      // and no error, which used to leave the picker dead for the whole
      // session. So offer the reload here too, not only on a thrown error.
      const listEmpty = options.length === 0;
      return (
        <div className="acombo-note">
          {needle
            ? `Nothing matches “${value.trim()}”. ${freeTextNote || 'It will be used as typed.'}`
            : emptyNote || 'Nothing on the list yet. Type a name to use it.'}
          {listEmpty && onRetry && (
            <>
              {' '}
              <button
                type="button"
                className="font-semibold text-avcs-primary underline"
                onMouseDown={(e) => e.preventDefault()}
                onClick={onRetry}
              >
                Reload the list
              </button>
            </>
          )}
        </div>
      );
    }
    return (
      <>
        {matches.map((option, index) => (
          <button
            key={option.key}
            type="button"
            role="option"
            aria-selected={index === active}
            className="acombo-option"
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(index)}
            onClick={() => choose(option)}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-avcs-text">{option.value}</span>
              {option.detail && <span className="block truncate text-xs text-avcs-muted">{option.detail}</span>}
            </span>
            {option.badge && (
              <span className="shrink-0 rounded-full border border-[#1e6bb824] bg-[#fbfeff] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-avcs-muted">
                {option.badge}
              </span>
            )}
          </button>
        ))}
      </>
    );
  })();

  return (
    <div className="acombo">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onFocus={openPanel}
        onClick={openPanel}
        onBlur={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        onChange={(e) => { onChange(e.target.value); openPanel(); }}
        onKeyDown={onKeyDown}
      />
      {value && !disabled && (
        <button
          type="button"
          className="acombo-clear"
          aria-label="Clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(''); openPanel(); }}
        >
          ×
        </button>
      )}
      {open && !(exact && matches.length <= 1) && (
        <div className="acombo-panel" role="listbox">{panel}</div>
      )}
    </div>
  );
}
